import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import { toISO, today, monthKeyFromDate } from "../../lib/dateUtils";
import { formatNumber, formatUSD } from "../../lib/formatters";

// Find the most recent FX rate at or before `monthKey`. Returns null if no
// rates exist. Same logic as TeamForm — duplicated rather than imported to
// keep the components self-contained.
function pickRate(fxRates, monthKey) {
  if (!fxRates || fxRates.length === 0) return null;
  const sorted = [...fxRates].sort((a, b) => a.month.localeCompare(b.month));
  const exact = sorted.find((r) => r.month === monthKey);
  if (exact) return exact;
  const prior = [...sorted].reverse().find((r) => r.month <= monthKey);
  if (prior) return prior;
  return sorted[0];
}

// Was this member active during the given month?
function activeIn(member, mKey) {
  if (!member?.startDate) return false;
  const monthStart = `${mKey}-01`;
  const [y, m] = mKey.split("-").map(Number);
  const nextY = m === 12 ? y + 1 : y;
  const nextM = m === 12 ? 1 : m + 1;
  const monthEndExclusive = `${nextY}-${String(nextM).padStart(2, "0")}-01`;
  if (member.startDate >= monthEndExclusive) return false;
  if (member.endDate && member.endDate < monthStart) return false;
  return true;
}

export default function BonusForm({ open, onClose, team = [], fxRates = [], onSave }) {
  const [memberId, setMemberId] = useState("");
  const [monthKey, setMonthKey] = useState(monthKeyFromDate(today()));
  const [inputCurrency, setInputCurrency] = useState("USD");
  const [amountInput, setAmountInput] = useState("");
  const [errors, setErrors] = useState({});

  // Reset form when opened
  useEffect(() => {
    if (!open) return;
    const firstActive = team.find((m) => m.status === "active");
    setMemberId(firstActive?.id || team[0]?.id || "");
    setMonthKey(monthKeyFromDate(today()));
    setInputCurrency("USD");
    setAmountInput("");
    setErrors({});
  }, [open, team]);

  // Pick the FX rate for the SELECTED month (not just today's) so historical
  // bonuses use the right conversion.
  const rate = useMemo(() => pickRate(fxRates, monthKey)?.usdToEgp || null, [fxRates, monthKey]);

  // Pre-fill amount if there's already a bonus for this member/month
  const selectedMember = team.find((m) => m.id === memberId);
  const existingUsd = selectedMember?.bonusByMonth?.[monthKey] || 0;

  // When member or month changes, seed amount from existing bonus (in USD)
  useEffect(() => {
    if (!open) return;
    if (existingUsd > 0) {
      setAmountInput(String(Math.round(existingUsd)));
      setInputCurrency("USD");
    } else {
      setAmountInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, monthKey, open]);

  const usdFromInput = useMemo(() => {
    const v = Number(amountInput);
    if (!Number.isFinite(v) || v <= 0) return 0;
    if (inputCurrency === "EGP" && rate && rate > 0) return v / rate;
    return v;
  }, [amountInput, inputCurrency, rate]);

  const toggleCurrency = (next) => {
    if (next === inputCurrency) return;
    if (rate && rate > 0) {
      const v = Number(amountInput);
      if (Number.isFinite(v) && v > 0) {
        const converted = next === "EGP" ? v * rate : v / rate;
        setAmountInput(String(Math.round(converted)));
      }
    }
    setInputCurrency(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!memberId) errs.member = "Pick a team member";
    if (!monthKey) errs.month = "Pick a month";
    if (!(Number(amountInput) > 0)) errs.amount = "Amount must be > 0";
    if (inputCurrency === "EGP" && (!rate || rate <= 0)) {
      errs.amount = "No FX rate for that month — add one in Settings → Currency exchange.";
    }
    if (selectedMember && !activeIn(selectedMember, monthKey)) {
      errs.month = "Member wasn't active that month.";
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    onSave(memberId, monthKey, Math.round(usdFromInput));
    onClose();
  };

  // Conversion preview
  let conversionHint = null;
  if (amountInput && Number(amountInput) > 0) {
    if (inputCurrency === "EGP") {
      if (rate && rate > 0) {
        conversionHint = (
          <span className="text-emerald-700 dark:text-emerald-300">
            ≈ {formatUSD(usdFromInput)} USD at {formatNumber(rate, 2)} EGP/USD
          </span>
        );
      } else {
        conversionHint = (
          <span className="text-red-600">No FX rate for {monthKey} — add one in Settings.</span>
        );
      }
    } else if (rate && rate > 0) {
      conversionHint = (
        <span className="text-brand-slate">
          ≈ {formatNumber(Number(amountInput) * rate, 0)} EGP at {formatNumber(rate, 2)} EGP/USD
        </span>
      );
    }
  }

  // Build a year+month dropdown of recent months for the picker (current
  // month + last 24 + next 6, plenty of room either way).
  const monthOptions = useMemo(() => {
    const out = [];
    const t = today();
    for (let i = -24; i <= 6; i++) {
      const d = new Date(t.getFullYear(), t.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      out.push({ key, label });
    }
    return out.reverse();
  }, []);

  return (
    <Modal open={open} onClose={onClose} title="Add bonus">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Team member *" value={memberId} onChange={(e) => setMemberId(e.target.value)} error={errors.member}>
            <option value="">— Select —</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}{m.role ? ` — ${m.role}` : ""}
              </option>
            ))}
          </Select>
          <Select label="Month *" value={monthKey} onChange={(e) => setMonthKey(e.target.value)} error={errors.month}>
            {monthOptions.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </Select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-brand-slate">Bonus amount</span>
            <div className="inline-flex items-center bg-gray-100 dark:bg-[var(--bg-soft)] rounded-md p-0.5 text-[10px] font-semibold">
              <button
                type="button"
                onClick={() => toggleCurrency("USD")}
                className={`px-2 py-0.5 rounded ${inputCurrency === "USD" ? "bg-white dark:bg-[var(--bg-surface)] text-brand-ink dark:text-[var(--text-primary)] shadow-sm" : "text-brand-slate"}`}
              >
                USD
              </button>
              <button
                type="button"
                onClick={() => toggleCurrency("EGP")}
                className={`px-2 py-0.5 rounded ${inputCurrency === "EGP" ? "bg-white dark:bg-[var(--bg-surface)] text-brand-ink dark:text-[var(--text-primary)] shadow-sm" : "text-brand-slate"}`}
              >
                EGP
              </button>
            </div>
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate text-sm pointer-events-none">
              {inputCurrency === "USD" ? "$" : "E£"}
            </span>
            <input
              type="number"
              step="1"
              min="0"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              placeholder="0"
              className={`w-full pl-8 pr-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] ${errors.amount ? "border-red-400" : "border-gray-200"}`}
            />
          </div>
          <div className="mt-1 min-h-[1rem] text-[11px]">
            {errors.amount ? <span className="text-red-600">{errors.amount}</span> : conversionHint}
          </div>
        </div>

        {existingUsd > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-2">
            Existing bonus for this month: <strong>{formatUSD(existingUsd)}</strong>. Saving will replace it.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">Save bonus</Button>
        </div>
      </form>
    </Modal>
  );
}
